import sourceMap from "source-map-js"
import SourceMapsLoader from "tensorbuzz-api/build/bug-reporting/source-maps-loader.js"

const {SourceMapConsumer} = sourceMap

// Import the loader directly: enableSourceMapsLoader() races its dynamic import in SDK 0.0.17-0.0.20.
export default class BrowserSourceMapsLoader extends SourceMapsLoader {
  loads = new Map()
  active = 0

  constructor(browser) {
    super()
    this.browser = browser
    const origins = new Set([browser.location.origin])

    for (const script of browser.document.querySelectorAll("script[src]")) {
      try {
        const url = new URL(script.src, browser.location.href)

        if (url.protocol === "https:" && url.pathname.startsWith("/packs/")) origins.add(url.origin)
      } catch {
        // A malformed script URL must not prevent reporting.
      }
    }
    this.sourceMapForSource(({url}) => {
      if (url.protocol === "https:" && origins.has(url.origin) && /^\/packs\/.*\.js$/.test(url.pathname)) {
        return `${url.origin}${url.pathname}.map`
      }
    })
  }

  async loadSourceMaps(error) {
    let timer

    try {
      // Only fetch maps referenced by this stack. Reports never queue behind other errors' symbolication.
      await Promise.race([
        Promise.all(this.getSourcesFromError(error).map((source) => this.loadSourceMapForSource(source))),
        new Promise((resolve) => {
          timer = this.browser.setTimeout(resolve, 1000)
        })
      ])
    } catch {
      // A malformed stack must still be reported.
    } finally {
      this.browser.clearTimeout(timer)
    }
  }

  loadSourceMapForSource({originalUrl, sourceMapUrl}) {
    if (this.loads.has(originalUrl)) return this.loads.get(originalUrl)
    if (this.active >= 4 || this.loads.size >= 64) return

    this.active++
    const loading = (async () => {
      const controller = new this.browser.AbortController()
      let timer

      try {
        const text = await Promise.race([
          this.browser.fetch(sourceMapUrl, {
            method: "GET",
            credentials: "omit",
            referrerPolicy: "no-referrer",
            redirect: "error",
            signal: controller.signal
          }).then(async (response) => {
            if (!response.ok) throw new Error("Source map unavailable")

            return await response.text()
          }),
          new Promise((_resolve, reject) => {
            timer = this.browser.setTimeout(() => {
              controller.abort()
              reject(new Error("Source map timed out"))
            }, 3000)
          })
        ])
        const consumer = new SourceMapConsumer(JSON.parse(text))
        const originalPositionFor = consumer.originalPositionFor.bind(consumer)

        // SDK 0.0.17 passes browser columns through unchanged. Consumers need zero-based lookup columns.
        consumer.originalPositionFor = (position) => {
          const original = originalPositionFor({...position, column: Math.max(0, position.column - 1)})

          return {...original, column: original.column == null ? null : original.column + 1}
        }
        this.sourceMaps.push({consumer, originalUrl})
      } catch {
        // Missing maps, failed requests and invalid map JSON all fall back to the original stack.
      } finally {
        this.browser.clearTimeout(timer)
        this.active--
      }
    })()

    this.loads.set(originalUrl, loading)

    return loading
  }

  parseStackTrace(stack) {
    try {
      const frames = super.parseStackTrace(stack)

      return frames.length ? frames : [stack]
    } catch {
      return [stack]
    }
  }
}
